import type {EmploymentsResponse, SelfEmploymentProfile, SelfEmploymentProvider} from '@rocket-ui/lk-api';
import {fetchEmployment} from '@rocket-ui/lk-api';
import type {MyTaxCheckingMode} from './myTaxChecking';
import {
	BIND_REQUEST_EXTERNAL_STATUS,
	getSelfEmploymentProvider,
	getSelfEmploymentSnapshot,
	isAlfaStyleSelfEmploymentProvider,
	isNotSelfEmployedEmploymentStatus,
} from './selfEmploymentProfiles';
import {RegistrationStep} from './steps';

type SelfEmploymentSnapshot = EmploymentsResponse['selfEmployment'] | SelfEmploymentProfile | undefined;

const getAgreementStatus = (selfEmployment: SelfEmploymentSnapshot): string | null | undefined => {
	if (!selfEmployment || !('agreement' in selfEmployment)) {
		return undefined;
	}

	return selfEmployment.agreement?.status;
};

const getExternalStatus = (selfEmployment: SelfEmploymentSnapshot): string | null | undefined => {
	if (!selfEmployment || !('externalStatus' in selfEmployment)) {
		return undefined;
	}

	return selfEmployment.externalStatus;
};

const isAlfaSelfEmployment = (selfEmployment: SelfEmploymentSnapshot): boolean => {
	if (selfEmployment && 'provider' in selfEmployment && selfEmployment.provider) {
		return isAlfaStyleSelfEmploymentProvider(selfEmployment.provider);
	}

	return isAlfaStyleSelfEmploymentProvider(getSelfEmploymentProvider());
};

const isPendingStatus = (selfEmployment: SelfEmploymentSnapshot): boolean => {
	return selfEmployment?.status === 'pending' || selfEmployment?.error === 'pending';
};

// TODO перепроврить надобность
export type SelfEmploymentCheckContext = {
	mode: MyTaxCheckingMode;
	/** Профиль СМЗ: `alfa` или `free_smz`. Без значения — провайдер домена. */
	provider?: SelfEmploymentProvider;
};

export const SELF_EMPLOYMENT_POLL_INTERVAL_MS = 3_000;
export const SELF_EMPLOYMENT_POLL_TIMEOUT_MS = 30_000;

const sleep = (ms: number, signal?: AbortSignal): Promise<void> => {
	return new Promise((resolve, reject) => {
		if (signal?.aborted) {
			reject(new DOMException('Aborted', 'AbortError'));
			return;
		}

		const timeoutId = setTimeout(resolve, ms);

		signal?.addEventListener(
			'abort',
			() => {
				clearTimeout(timeoutId);
				reject(new DOMException('Aborted', 'AbortError'));
			},
			{once: true},
		);
	});
};

export type SelfEmploymentIntroAction =
	| {type: 'proceed_to_inn'}
	| {type: 'check_inn_or_wait_fns'}
	| {type: 'not_selfemployed'}
	| {type: 'no_permissions'}
	| {type: 'fns_pending'}
	| {type: 'sign_agreement'}
	| {type: 'load_error'};

export type SelfEmploymentCheckOptions = {
	mode: MyTaxCheckingMode;
	/** Только GET /employment (poll), без POST /self_employment/validation */
	skipValidation?: boolean;
};

/** Пока идёт check inn: poll GET /employment только при just_sent_inn_for_check. */
export const isSelfEmploymentInnCheckPollingPending = (
	selfEmployment: EmploymentsResponse['selfEmployment'] | undefined,
): boolean => {
	return selfEmployment?.status === 'just_sent_inn_for_check';
};

/** После 30 с poll: signed + just_sent_inn → «Ждём ответ ФНС»; sent → список, чтобы подписать соглашение. */
export const resolveSelfEmploymentPollTimeoutStep = (
	selfEmployment: SelfEmploymentSnapshot,
	context: SelfEmploymentCheckContext,
): RegistrationStep => {
	if (selfEmployment?.status === 'just_sent_inn_for_check') {
		const agreementStatus = getAgreementStatus(selfEmployment);

		if (agreementStatus === 'sent') {
			return RegistrationStep.WorkStatus;
		}

		if (agreementStatus === 'signed') {
			return RegistrationStep.MyTaxFnsPending;
		}
	}

	if (context.mode === 'permissions') {
		if (context.provider === 'free_smz') {
			return RegistrationStep.MyTaxFnsPending;
		}

		return RegistrationStep.MyTaxAccess;
	}

	return resolveSelfEmploymentStep(selfEmployment, context);
};

/**
 * Маппинг GET /employment на экране MyTaxIntro «Приступить» (до validation).
 */
export const resolveSelfEmploymentIntroAction = (
	selfEmployment: SelfEmploymentSnapshot,
): SelfEmploymentIntroAction => {
	if (!selfEmployment) {
		return {type: 'load_error'};
	}

	const {status, error, validated} = selfEmployment;
	const agreementStatus = getAgreementStatus(selfEmployment);
	const externalStatus = getExternalStatus(selfEmployment);

	if (agreementStatus === 'sent') {
		return {type: 'sign_agreement'};
	}

	if (
		agreementStatus === 'signed' &&
		isPendingStatus(selfEmployment) &&
		externalStatus === BIND_REQUEST_EXTERNAL_STATUS
	) {
		// free_smz не останавливаем на «Нужен доступ в «Мой налог»».
		return selfEmployment && 'provider' in selfEmployment && selfEmployment.provider === 'free_smz'
			? {type: 'proceed_to_inn'}
			: {type: 'no_permissions'};
	}

	if (agreementStatus === 'signed' && isPendingStatus(selfEmployment) && isAlfaSelfEmployment(selfEmployment)) {
		return {type: 'fns_pending'};
	}

	if (status === 'need_permissions' || error === 'need_permissions' || status === 'pending' || error === 'pending') {
		return selfEmployment && 'provider' in selfEmployment && selfEmployment.provider === 'free_smz'
			? {type: 'proceed_to_inn'}
			: {type: 'no_permissions'};
	}

	if (
		isNotSelfEmployedEmploymentStatus(status) ||
		isNotSelfEmployedEmploymentStatus(error) ||
		error === 'withdrawn'
	) {
		return {type: 'not_selfemployed'};
	}

	if (status === 'just_sent_inn_for_check' && agreementStatus === 'signed') {
		return {type: 'fns_pending'};
	}

	if (status === 'just_sent_inn_for_check') {
		return {type: 'check_inn_or_wait_fns'};
	}

	if (status === 'unknown' || status === 'validated' || validated) {
		return {type: 'proceed_to_inn'};
	}

	return {type: 'load_error'};
};

/**
 * Маппинг ответа GET /employment → экран СМЗ после POST /self_employment/validation.
 *
 * Оркестрация запросов:
 * - MyTaxIntro «Приступить» → GET /employment → resolveSelfEmploymentIntroAction
 * - После PATCH inn → POST /self_employment/bind_partner → СНИЛС / код резидента
 * - После документов → POST /self_employment/validation + GET /employment (poll)
 * - «Доступ предоставлен» / «Перейти в Мой налог» → POST /self_employment/validation
 */
export const resolveSelfEmploymentStep = (
	selfEmployment: EmploymentsResponse['selfEmployment'] | SelfEmploymentProfile | undefined,
	context: SelfEmploymentCheckContext,
): RegistrationStep => {
	if (!selfEmployment) {
		return RegistrationStep.MyTaxLoadError;
	}

	const {status, error, validated} = selfEmployment;
	const agreementStatus = getAgreementStatus(selfEmployment);
	const externalStatus = getExternalStatus(selfEmployment);

	// Неподписанное соглашение: на список, клик открывает фрейм Альфа.
	if (agreementStatus === 'sent') {
		return RegistrationStep.WorkStatus;
	}

	// status/error важнее validated: на staging validated:true может идти вместе с need_permissions.
	// free_smz этот экран ломает: доступ в «Мой налог» для него не запрашиваем.
	if (status === 'need_permissions' || error === 'need_permissions') {
		if (context.provider === 'free_smz') {
			return RegistrationStep.MyTaxFnsPending;
		}

		return context.mode === 'permissions'
			? RegistrationStep.MyTaxAccessError
			: RegistrationStep.MyTaxAccess;
	}

	if (isPendingStatus(selfEmployment)) {
		if (context.provider === 'free_smz') {
			return RegistrationStep.MyTaxFnsPending;
		}

		if (agreementStatus === 'signed' && externalStatus === BIND_REQUEST_EXTERNAL_STATUS) {
			return RegistrationStep.MyTaxAccess;
		}

		if (agreementStatus === 'signed' && isAlfaSelfEmployment(selfEmployment)) {
			return RegistrationStep.MyTaxFnsPending;
		}

		return RegistrationStep.MyTaxAccess;
	}

	if (status === 'just_sent_inn_for_check' && agreementStatus === 'signed') {
		return RegistrationStep.MyTaxFnsPending;
	}

	if (
		status === 'unknown' ||
		isNotSelfEmployedEmploymentStatus(status) ||
		isNotSelfEmployedEmploymentStatus(error) ||
		error === 'withdrawn'
	) {
		return RegistrationStep.MyTaxNotSelfEmployed;
	}

	if (status === 'validated' || validated) {
		return RegistrationStep.MyTaxSuccess;
	}

	return RegistrationStep.MyTaxLoadError;
};

export const fetchSelfEmploymentStep = async (
	context: SelfEmploymentCheckContext,
	signal?: AbortSignal,
): Promise<RegistrationStep> => {
	const {data} = await fetchEmployment(signal);

	return resolveSelfEmploymentStep(getSelfEmploymentSnapshot(data, context.provider), context);
};

/** GET /employment каждые 3 с до 30 с на check inn; по таймауту — «Ждём ответ ФНС». */
export const fetchSelfEmploymentStepWithPolling = async (
	context: SelfEmploymentCheckContext,
	signal?: AbortSignal,
): Promise<RegistrationStep> => {
	const deadline = Date.now() + SELF_EMPLOYMENT_POLL_TIMEOUT_MS;

	while (true) {
		const {data} = await fetchEmployment(signal);
		const selfEmployment = getSelfEmploymentSnapshot(data, context.provider);
		const agreementStatus = getAgreementStatus(selfEmployment);

		if (selfEmployment?.status === 'just_sent_inn_for_check' && agreementStatus === 'signed') {
			return RegistrationStep.MyTaxFnsPending;
		}

		if (agreementStatus === 'sent') {
			return RegistrationStep.WorkStatus;
		}

		if (!isSelfEmploymentInnCheckPollingPending(selfEmployment)) {
			return resolveSelfEmploymentStep(selfEmployment, context);
		}

		if (Date.now() >= deadline) {
			return resolveSelfEmploymentPollTimeoutStep(selfEmployment, context);
		}

		await sleep(SELF_EMPLOYMENT_POLL_INTERVAL_MS, signal);
	}
};
